import { z } from 'zod';
import { contentIdSchema } from './common.js';
/**
 * Контракт Live TV (IPTV).
 *
 * Граница та же, что и у остальных источников: адрес IPTV-провайдера, его
 * плейлист, токены в URL каналов и адреса сегментов остаются server-side.
 * Клиент знает только наш `channelId` и общий путь playback.
 *
 * Это существенно, а не косметично: ссылки IPTV-провайдеров обычно содержат
 * учётные данные прямо в URL, и один такой URL в DTO раздал бы подписку всем,
 * кто открыл вкладку разработчика.
 */
/** Непрозрачный id канала: производная от upstream URL, не сам URL. */
export const liveChannelIdSchema = z.string().regex(/^[0-9a-f]{16}$/, 'invalid channel id');
/**
 * Приставка `contentId` телеканала.
 *
 * Канал ходит через тот же playback, что фильм, и отличается от него ровно
 * этой приставкой. Раз по ней принимаются решения (история эфира, подпись в
 * мониторе, избранное), она объявлена один раз здесь, а не переписана строкой
 * в каждом месте, где спрашивают «а это не канал ли».
 */
export const CHANNEL_CONTENT_PREFIX = 'local:tv:';
/** Наш id канала из `contentId`; `null` — это не канал. */
export function channelIdOf(contentId) {
    if (!contentId.startsWith(CHANNEL_CONTENT_PREFIX))
        return null;
    const id = contentId.slice(CHANNEL_CONTENT_PREFIX.length);
    return liveChannelIdSchema.safeParse(id).success ? id : null;
}
export const liveChannelSchema = z.object({
    id: liveChannelIdSchema,
    name: z.string(),
    /** Группа плейлиста (`group-title`), если провайдер её указал. */
    group: z.string().nullable(),
    /** Номер канала из `tvg-chno`, если он есть: по нему привычно листать. */
    number: z.number().int().positive().nullable(),
    /** Логотип отдаётся через наш image proxy, а не ссылкой на провайдера. */
    logoUrl: z.string().nullable(),
    /**
     * Идентификатор канала в телепрограмме (`tvg-id` плейлиста; у Skaz — `chNNN`).
     * По нему канал сопоставляется с EPG. `null` — у канала нет расписания.
     * Это не секрет и не адрес потока: сам id программы, а не ссылка на неё.
     */
    epgId: z.string().nullable(),
    /**
     * Идентификатор канала как контента. Через него канал проходит тот же
     * playback, что и фильм: сессия, права, TTL и Media Gateway — общие.
     */
    contentId: contentIdSchema,
    /**
     * Канал в избранном у ЭТОГО зрителя.
     *
     * Не свойство плейлиста, а ответ на вопрос «мой ли он»: список каналов и так
     * идёт с сервера, и отметить его там дешевле, чем тянуть избранное вторым
     * запросом и сводить два списка на клиенте — у Skaz их под две тысячи.
     */
    favorite: z.boolean().default(false),
});
/**
 * Одна передача телепрограммы. Время — Unix-миллисекунды UTC: клиент сам
 * переводит в локальную зону, а сервер не гадает про часовой пояс зрителя.
 */
export const epgProgramSchema = z.object({
    /** Начало передачи, Unix ms. */
    start: z.number().int(),
    /** Конец передачи, Unix ms. */
    stop: z.number().int(),
    title: z.string(),
    /** Описание передачи; пусто у многих каналов. */
    description: z.string().nullable(),
});
/**
 * «Сейчас в эфире» и «далее» для одного канала — то, что показывается на
 * карточке канала. Оба поля могут быть `null`: расписания может не быть, а у
 * последней известной передачи нет «следующей».
 */
export const epgNowNextSchema = z.object({
    now: epgProgramSchema.nullable(),
    next: epgProgramSchema.nullable(),
});
/**
 * Пакетный ответ «сейчас/далее»: карта `epgId → now/next`. Пакетом, а не по
 * каналу, потому что страница каналов рисует сразу весь список, и отдельный
 * запрос на каждый канал превратил бы открытие в сотни round-trip.
 */
export const epgNowNextResponseSchema = z.object({
    channels: z.record(z.string(), epgNowNextSchema),
});
/** Предельные размеры запроса окна телепрограммы. */
export const EPG_WINDOW_MAX_IDS = 80;
export const EPG_WINDOW_MAX_SPAN_MS = 12 * 60 * 60 * 1000;
/** Насколько окно может уходить от «сейчас»: назад и вперёд. */
export const EPG_WINDOW_MAX_PAST_MS = 24 * 60 * 60 * 1000;
export const EPG_WINDOW_MAX_FUTURE_MS = 48 * 60 * 60 * 1000;
/** Запрос телепрограммы многих каналов за окно времени (Unix ms, `[from, to)`). */
export const epgWindowRequestSchema = z.object({
    ids: z.array(z.string()).max(EPG_WINDOW_MAX_IDS),
    from: z.number().int(),
    to: z.number().int(),
});
/**
 * Ответ окна: карта `epgId → передачи`, пересекающие окно, по порядку времени.
 * Описаний нет (`description: null`) — они берутся из расписания одного канала.
 */
export const epgWindowResponseSchema = z.object({
    channels: z.record(z.string(), z.array(epgProgramSchema)),
});
/** Полное расписание канала — для экрана телегида. */
export const epgScheduleResponseSchema = z.object({
    epgId: z.string(),
    programs: z.array(epgProgramSchema),
});
export const liveChannelListResponseSchema = z.object({
    channels: z.array(liveChannelSchema),
    /** Честное объяснение пустого списка вместо тихой пустоты. */
    notice: z.string().nullable(),
});
//# sourceMappingURL=livetv.js.map